import { type ClientId } from '../client/bridge-api-client-types';
import {
	type CommandConstructor,
	type CommandProperties,
} from '../command/bridge-api-command-types';
import { emitter } from '../events/bridge-events-emitter';
import type { RPCIdentifier } from '../rpc/bridge-api-rpc-message';

import {
	MAX_MESSAGES_PER_RECEIVER,
	MAX_TOTAL_MESSAGES,
	MESSAGE_LIFETIME_IN_MILLIS,
	MESSAGE_TICK_TIME_IN_MILLIS,
} from './bridge-api-message-queue-constants';

/* eslint-disable no-console */

/**
 * An invocation which was unable to be sent to {@link receiverId} before, due to the receiver
 * not being in a ready state. It is maintained in the {@link BufferedInvocationQueue}, and flushed
 * once the receiver becomes ready. If a receiver is not ready within a time limit of
 * {@link MESSAGE_LIFETIME_IN_MILLIS}, then all messages tied to the receiver are dropped.
 */
export type BufferedInvocation = {
	/**
	 * Unique identifier for this specific command invocation.
	 */
	invocationId: string;
	/**
	 * Client who is attempting to communicate with {@link receiverId}, with a {@link command}.
	 */
	senderId: ClientId;
	/**
	 * Client who is intended on receiving and handling {@link command}.
	 */
	receiverId: ClientId;
	/**
	 * The command which {@link senderId} is attempting to send to {@link receiverId}.
	 */
	command: CommandConstructor<any, any>;
	/**
	 * Parameters of the command invocation.
	 * @description NOTE: It is assumed command properties are not sensitive to the time in which they
	 * are sent. I.e., the expected result is the same irrespective of when it is sent. This is to support
	 * this behaviour of buffering the command if the receiver is not ready.
	 */
	params: CommandProperties;
	/**
	 * Time when the invocation was enqueued for latter invocation. Used for computing lifetime.
	 * Uses performance.now() for stable timing that's not affected by system clock changes.
	 */
	enqueuedAt: number;
};

/**
 * Maintains a queue of buffered invocations which were unable to send due to the receiver of the message
 * not being ready to receive them from the current bridge API instance.
 *
 * I.e., if JS context 1 mounts 10 seconds before JS context 2, and JS context 1 sends a message - it will
 * not be handle-able by JS context 2. Hence, we need to wait until JS context 2 is ready & then flush all
 * messages which JS context 1 was trying to send to JS context 2 *in the order they came*.
 *
 * But, also: We cannot hold onto messages forever. This is to avoid memory leaks, where we continue to generate
 * messages (e.g. via network streams), and an intended recipient is not reachable.
 *
 * {@link BufferedInvocationQueue} manages this set of requirements.
 */
export class BufferedInvocationQueue {
	/**
	 * A queue of messages keyed against a recipient client ID.
	 *
	 * @description Once the receiver is ready, we will flush all messages to them, IF they have not expired.
	 */
	private queue = new Map<ClientId, BufferedInvocation[]>();

	/**
	 * A lazy tick which is executed only if there are buffered messages, and cleans up a message after
	 * {@link MESSAGE_LIFETIME_IN_MILLIS} have elapsed since the message was enqueued.
	 *
	 * @description NOTE: It is important to ensure there is only one interval which can run at any given time.
	 * Too many timers can clog up the event loop and slow execution:
	 */
	private tick: ReturnType<typeof setInterval> | undefined = undefined;

	/**
	 * Optional callback that gets called on every tick with unique senderId/receiverId pairs
	 * for clients that are still waiting to connect.
	 */
	private onTickCallback?: (
		pendingConnections: Array<{ senderId: ClientId; receiverId: ClientId }>,
	) => void;

	/**
	 * Optional callback that gets called when a message is dropped due to expiration or queue overflow.
	 * @description Use this to perform any clean up activities relative to a buffered command.
	 */
	private onInvocationDropped?: (invocation: BufferedInvocation) => void;

	/**
	 * Buffers an invocation into a queue tied to the intended {@link receiverId}.
	 *
	 * @description Once the receiver is reachable, this message will be flushed alongside any other messages
	 * which were previously enqueued against the provided {@link receiverId}.
	 *
	 * @param invocationId
	 * @param senderId
	 * @param receiverId
	 * @param command
	 * @param params
	 */
	public enqueue(
		invocationId: string,
		senderId: ClientId,
		receiverId: ClientId,
		command: CommandConstructor<any, any>,
		params: CommandProperties,
	): void {
		const queue = this.queue.get(receiverId) ?? [];
		const now = performance.now();
		const groupId = params.groupId;

		// check the receiver is not overwhelmed with too many pending messages; if it is, drop the oldest message
		if (queue.length >= MAX_MESSAGES_PER_RECEIVER) {
			console.warn(`Message queue for receiver ${receiverId} is full, dropping oldest message`);
			const expired = queue.shift();
			if (expired) {
				this.onInvocationDropped?.(expired);
			}
		}

		// check if the overall queue isn't overwhelmed; if it is, drop the oldest message from each receiver
		if (this.messageCount >= MAX_TOTAL_MESSAGES) {
			console.warn('Total message queue is full, dropping oldest messages');
			this.checkAndDropOldestMessage();
		}

		// If this command has a groupId, reset the timer for all commands with the same groupId
		if (groupId) {
			this.resetGroupTimers(groupId, now);
		}

		queue.push({
			invocationId,
			senderId,
			receiverId,
			command,
			params,
			enqueuedAt: now,
		});

		this.queue.set(receiverId, queue);

		emitter().emit({
			type: 'message-queued',
			messageCount: this.messageCount,
			messageId: invocationId,
			clientId: receiverId,
			reason: 'receiver-not-ready',
		});

		// now that we have >1 message (at least), start a tick which executes every N millis
		// and drop any messages which go beyond the lifetime limit
		this.startTick();
	}

	/**
	 * Flush all messages associated with a speciifc receiver.
	 *
	 * @description Typically called once the receiver is marked as ready.
	 *
	 * @param receiverId unique identifier for the receiver
	 */
	public flush(receiverId: ClientId) {
		const messages = this.queue.get(receiverId);

		// no messages found
		if (!messages) {
			return;
		}

		// we know we have a reference in the message list now - remove it
		this.deleteClientFromQueue(receiverId);

		// empty message list
		if (messages.length === 0) {
			return;
		}

		// emit queue flush event
		emitter().emit({
			type: 'queue-flushed',
			clientId: receiverId,
			messageCount: messages.length,
			reason: 'client-ready',
		});

		// return no-empty message list
		return messages;
	}

	/**
	 * Returns the total number of messages across all receivers.
	 * @returns count of messages across all buffered receivers
	 */
	get messageCount() {
		return Array.from(this.queue.values()).reduce((sum, q) => sum + q.length, 0);
	}

	/**
	 * Registers a callback to be called on every tick with unique senderId/receiverId pairs
	 * for clients that are still waiting to connect.
	 *
	 * @param callback Function to call with pending connection pairs
	 */
	public onTick(
		callback: (pendingConnections: Array<{ senderId: ClientId; receiverId: ClientId }>) => void,
	): void {
		this.onTickCallback = callback;
	}

	/**
	 * Registers a callback to be called when a message is dropped due to expiration or queue overflow.
	 *
	 * @param callback Callback to invoke with the dropped message
	 */
	public onInvocationDroppped(callback: (invocation: BufferedInvocation) => void): void {
		this.onInvocationDropped = callback;
	}

	/**
	 * Drops all buffered messages for a specific client (both as sender and receiver).
	 *
	 * @param clientId unique identifier for the client whose messages should be dropped
	 * @returns number of messages that were dropped
	 */
	public clear(clientId: ClientId): number {
		let droppedCount = 0;
		const toDelete = new Set<ClientId>();

		// remove all messages where this client is the receiver
		if (this.queue.has(clientId)) {
			const messages = this.queue.get(clientId) ?? [];

			if (messages.length > 0) {
				emitter().emit({
					type: 'queue-cleared',
					clientId,
					messageCount: this.messageCount - messages.length,
					reason: 'client-teardown',
				});
			}

			// notify before dropping messages
			if (this.onInvocationDropped) {
				for (const message of messages) {
					this.onInvocationDropped(message);
				}
			}

			droppedCount += messages.length;

			// delete immediately - we do not want to process this queue below
			this.queue.delete(clientId);
		}

		// remove all messages where this client is the sender
		for (const [receiverId, messages] of this.queue) {
			const messagesLength = messages.length;
			const messagesToKeep = messages.filter((message) => {
				if (message.senderId === clientId) {
					// notify before dropping message
					if (this.onInvocationDropped) {
						this.onInvocationDropped(message);
					}

					return false;
				}
				return true;
			});
			const removedCount = messagesLength - messagesToKeep.length;

			if (removedCount > 0) {
				droppedCount += removedCount;

				if (messagesToKeep.length === 0) {
					toDelete.add(receiverId);
				} else {
					this.queue.set(receiverId, messagesToKeep);
				}
			}
		}

		// clean up empty queues and stop tick
		this.deleteClientsFromQueue(toDelete);
		return droppedCount;
	}

	/**
	 * Drops a specific buffered message by its invocation ID.
	 *
	 * @param invocationId The unique identifier of the message to drop
	 * @returns true if the message was found and dropped, false otherwise
	 */
	public cancel(invocationId: RPCIdentifier): boolean {
		for (const [receiverId, messages] of this.queue) {
			const messageIndex = messages.findIndex((msg) => msg.invocationId === invocationId);

			if (messageIndex !== -1) {
				const droppedMessage = messages[messageIndex];

				// notify before dropping message
				if (this.onInvocationDropped) {
					this.onInvocationDropped(droppedMessage);
				}

				// remove the message
				messages.splice(messageIndex, 1);

				// if no more messages for this receiver, clean up
				if (messages.length === 0) {
					this.deleteClientFromQueue(receiverId);
				}

				return true;
			}
		}

		return false;
	}

	/**
	 * Cancels all buffered messages that match a specific group ID.
	 *
	 * @param groupId The group identifier of messages to cancel
	 * @returns number of messages that were cancelled
	 */
	public cancelGroup(groupId: string): number {
		let droppedCount = 0;
		const toDelete = new Set<ClientId>();

		for (const [receiverId, messages] of this.queue) {
			const messagesToKeep = messages.filter((message) => {
				if (message.params.groupId === groupId) {
					// notify before dropping message
					if (this.onInvocationDropped) {
						this.onInvocationDropped(message);
					}
					droppedCount++;
					return false;
				}
				return true;
			});

			if (messagesToKeep.length === 0) {
				toDelete.add(receiverId);
			} else if (messagesToKeep.length !== messages.length) {
				this.queue.set(receiverId, messagesToKeep);
			}
		}

		// clean up empty queues
		this.deleteClientsFromQueue(toDelete);
		return droppedCount;
	}

	/**
	 * Clear all state relevant to the queue.
	 */
	public destroy(): void {
		this.queue.clear();

		if (this.tick) {
			clearInterval(this.tick);
			this.tick = undefined;
		}
	}

	/**
	 * Checks the lifetime of all messages in the queue and drops them if they have exceeded their
	 * maximum allowed lifetime of {@link MESSAGE_LIFETIME_IN_MILLIS}.
	 */
	private checkLifetimeAndDrop() {
		const now = performance.now();
		const toDelete = new Set<ClientId>();

		// cull all messages for each receiver which have expired
		for (const [receiverId, messages] of this.queue) {
			let needle = 0;

			// find the first message that has not expired (since messages are chronologically ordered)
			for (let idx = 0; idx < messages.length; idx++) {
				const waitTime = now - messages[idx].enqueuedAt;

				if (waitTime <= MESSAGE_LIFETIME_IN_MILLIS) {
					needle = idx;
					break;
				} else {
					needle = messages.length;
				}
			}

			// remove all expired messages in one sweep
			if (needle > 0) {
				// notify before dropping messages
				if (this.onInvocationDropped) {
					for (let idx = 0; idx < needle; idx++) {
						this.onInvocationDropped(messages[idx]);
					}
				}

				// remove expired messages
				messages.splice(0, needle);
			}

			// if we no longer have any messages, we should clear this entry entirely
			if (messages.length === 0) {
				toDelete.add(receiverId);
			}
		}

		// clean up all empty entries
		this.deleteClientsFromQueue(toDelete);
	}

	/**
	 * Drops the oldest messages across all receivers when the total queue size is exceeded.
	 */
	private checkAndDropOldestMessage(): void {
		let oldest = Infinity;
		let receiver: ClientId | null = null;

		for (const [receiverId, messages] of this.queue) {
			if (messages.length > 0 && messages[0].enqueuedAt < oldest) {
				oldest = messages[0].enqueuedAt;
				receiver = receiverId;
			}
		}

		// drop the oldest message
		if (receiver) {
			const queue = this.queue.get(receiver) ?? [];
			queue.shift();

			// if no more messages - clean up
			if (queue.length === 0) {
				this.deleteClientFromQueue(receiver);
			}
		}
	}

	/**
	 * Starts an interval which does a full sweep every {@link MESSAGE_TICK_TIME_IN_MILLIS} to drop messages
	 * which have gone beyond their lifetime.
	 *
	 * @description NOTE: The use of {@link setInterval} is deliberate here, as browsers will try their best
	 * to continue executing {@link setInterval} callbacks even if a tab is unfocused. If the tab enters the
	 * 'frozen' state in Chromium browsers, then execution is completely paused as per:
	 * @see {@link https://developer.chrome.com/docs/web-platform/page-lifecycle-api}
	 */
	private startTick = () => {
		if (this.tick) {
			return;
		}

		this.tick = setInterval(() => {
			this.checkLifetimeAndDrop();

			// Call the onTick callback if registered and there are buffered messages
			if (this.onTickCallback && this.queue.size > 0) {
				const pendingConnections = this.getUniquePendingConnections();
				this.onTickCallback(pendingConnections);
			}
		}, MESSAGE_TICK_TIME_IN_MILLIS);
	};

	/**
	 * Stops the tick if there are no more messages to process.
	 */
	private stopTick = () => {
		if (this.queue.size > 0) {
			return;
		}

		clearInterval(this.tick);
		this.tick = undefined;
	};

	/**
	 * Resets the enqueuedAt time for all messages with the specified groupId across all receivers.
	 * This ensures that when a new message with a groupId is enqueued, all messages in the same
	 * group get their timer reset, allowing the entire group to stay alive for MESSAGE_LIFETIME_IN_MILLIS
	 * after the last message in the group.
	 *
	 * @param groupId The group identifier to reset timers for
	 * @param newTime The new enqueuedAt time to set for all messages in the group
	 */
	private resetGroupTimers(groupId: string, newTime: number): void {
		for (const messages of this.queue.values()) {
			for (const message of messages) {
				if (message.params.groupId === groupId) {
					message.enqueuedAt = newTime;
				}
			}
		}
	}

	/**
	 * Extracts unique senderId/receiverId pairs from all buffered messages.
	 * @returns Array of unique sender/receiver pairs that are still waiting to connect
	 */
	private getUniquePendingConnections(): Array<{ senderId: ClientId; receiverId: ClientId }> {
		const uniquePairs = new Map<string, { senderId: ClientId; receiverId: ClientId }>();

		for (const messages of this.queue.values()) {
			for (const message of messages) {
				const key = `${message.senderId}:${message.receiverId}`;
				if (!uniquePairs.has(key)) {
					uniquePairs.set(key, {
						senderId: message.senderId,
						receiverId: message.receiverId,
					});
				}
			}
		}

		return Array.from(uniquePairs.values());
	}

	/**
	 * Safely deletes a single client from the queue and handles cleanup.
	 *
	 * @param clientId The client to remove from the queue
	 * @private
	 */
	private deleteClientFromQueue(clientId: ClientId): void {
		this.queue.delete(clientId);
		this.stopTick();
	}

	/**
	 * Safely deletes multiple clients from the queue and handles cleanup.
	 *
	 * @param clientIds Set of client IDs to remove from the queue
	 * @private
	 */
	private deleteClientsFromQueue(clientIds: Set<ClientId>): void {
		for (const clientId of clientIds) {
			this.queue.delete(clientId);
		}
		this.stopTick();
	}
}
